import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";

/**
 * The count on a destination that has work waiting on it.
 *
 * **Rendered from one place because both surfaces render it**, for the reason
 * `endUserDestinations` exists: the top nav and the dashboard's action cards had
 * a badge each, and a fix applied to one of them is the drift that module was
 * built to remove. They differ only in size, so that is the only prop.
 *
 * **The numeral is hidden and the sentence is not** (NAV-28, reviewed on PR
 * #100). The first version put `aria-label` on the badge's own `<span>`, which
 * ARIA does not permit: a plain `span` is `role="generic"`, naming is prohibited
 * on it, and a conforming reader drops the label — so the sentence this branch
 * had just renamed (`nav.waitingOnYouBadge`) may never have been announced at
 * all, and what came through was the bare "3". Hiding the digit and putting the
 * words in a visually-hidden span needs no role and no live region: the badge
 * sits inside the entry's own link, so the link's accessible name composes to
 * "Waiting on you, 3 things waiting on you", which is the whole fact.
 *
 * Deliberately *not* `role="status"`. That is a live region — it announces
 * changes as they arrive — and this is a count somebody reads when they get to
 * it, not an event. A live region here would interrupt whatever they were doing
 * every time a colleague raised a request.
 */
export function WaitingBadge({
  count,
  size = "nav",
  testId,
}: {
  count: number;
  /** The nav bar's is smaller than the dashboard card's; nothing else differs. */
  size?: "nav" | "card";
  testId: string;
}) {
  const { t } = useTranslation("navigation");
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-semibold text-destructive-foreground",
        size === "nav" ? "ml-0.5 h-5 min-w-5" : "h-6 min-w-6",
      )}
      data-testid={testId}
    >
      <span aria-hidden="true">{count}</span>
      <span className="sr-only">{t("nav.waitingOnYouBadge", { count })}</span>
    </span>
  );
}
